import React from 'react';

import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Cell} from 'notebook/tables/constants/CellConstants';
import ErrorTriangle, {TriangleLocation, TriangleState} from 'notebook/tables/errors/ErrorTriangle';
import notifications from 'notifications';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {EXPERIMENT_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import {
  BaseCondition,
  Condition,
  ConditionGroup,
  ConditionValidationState,
  getMethodIdsImplementingConditionGroup,
  isConditionGroup,
  isPersistedCondition,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {NEAR_BLACK} from 'ui-library/Colors';
import * as modals from 'util/Modals';
import {Optional} from 'util/Types';

import {
  buildDraftConditionGroupPmdvMap,
  buildValidationStatesForUpdate,
  ConditionDraft,
} from './conditionUtils';

/**
 * The subset of a table {@link Cell} these replicate helpers read. Text may be nullish at runtime
 * (blank or absent cells), unlike `Cell`'s stricter `text: string`.
 */
interface ReplicateCountCell {
  text: Optional<string>;
}

/**
 * A fully archived condition has a 0 replicate count. Preserve that 0 through unrelated table edits
 * so re-validation does not treat it as invalid and reset it with a spurious notification. Only
 * preserve when the row maps to an already-archived condition whose cell still reads 0, so editing a
 * live condition down to 0 still validates as before.
 */
export function keepFullyArchivedZeroReplicates(
  cell: ReplicateCountCell,
  isNewRow: boolean,
  originalCondition: BaseCondition | undefined
): boolean {
  if (isNewRow || originalCondition == null || originalCondition.numReplicates !== 0) {
    return false;
  }
  return (cell.text ?? '').trim() === '0';
}

export function getReplicateCellValue(cell: ReplicateCountCell): number | null {
  if (cell.text == null) {
    return null;
  }
  const value = Number.parseInt(cell.text, 10);
  if (Number.isNaN(value) || value <= 0) {
    notifications.error(
      `The number of replicates was reset to 1 since the specified value was not a whole number greater than 0`,
      {id: 'ReplicateTable-replicate-bounds'}
    );
    return 1;
  }
  if (value > 999) {
    notifications.error(
      `The number of replicates was reset to 999 since the specified value exceeded the maximum`,
      {
        id: 'ReplicateTable-replicate-bounds',
      }
    );
    return 999;
  }
  return value;
}

/**
 * Resolves the replicate count for an edited table cell. A fully archived condition keeps its 0
 * count (see {@link keepFullyArchivedZeroReplicates}). Every other cell is validated and clamped by
 * {@link getReplicateCellValue}, defaulting to 1 when blank.
 */
export function resolveReplicateCount(
  numReplicatesCell: ReplicateCountCell,
  isNewRow: boolean,
  originalCondition: BaseCondition | undefined
): number {
  if (keepFullyArchivedZeroReplicates(numReplicatesCell, isNewRow, originalCondition)) {
    return 0;
  }
  return getReplicateCellValue(numReplicatesCell) ?? 1;
}

/**
 * Returns the condition name for a table row/column, falling back to a positional default name when
 * the name cell is blank.
 */
export function resolveConditionName(
  nameText: Optional<string>,
  index: number,
  procedureType: ProcedureType
): string {
  if (nameText == null || nameText === '') {
    return `${getProcedureRunConditionTextForProcedureType(procedureType, {capital: true})} ${index + 1}`;
  }
  return nameText;
}

export function makeValueCell(
  value: number,
  index: number,
  locked: boolean,
  isLockedPerCondition: Array<boolean>
): Cell {
  return {
    text: value.toString(),
    locked: locked || isLockedPerCondition[index],
    align: 'right',
    foregroundColor: NEAR_BLACK,
    bold: false,
  };
}

export function getNameErrorTriangle(
  validationStatePerCondition: Array<ConditionValidationState>,
  index: number,
  procedureType: ProcedureType
): Optional<JSX.Element> {
  if (validationStatePerCondition[index].isNameLengthOverLimit) {
    return (
      <ErrorTriangle
        dataTestAttrs={{
          component: 'ReplicateTable',
          element: 'nameValidation',
          index: index.toString(),
        }}
        state={TriangleState.ILLEGAL}
        error={{
          type: '',
          message: `${getProcedureRunConditionTextForProcedureType(procedureType, {
            capital: true,
          })} name may not exceed ${EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_NAME_MAX_LENGTH} characters`,
        }}
        location={TriangleLocation.CELL}
      />
    );
  } else if (validationStatePerCondition[index].isDuplicateName) {
    return (
      <ErrorTriangle
        dataTestAttrs={{
          component: 'ReplicateTable',
          element: 'nameValidation',
          index: index.toString(),
        }}
        state={TriangleState.ILLEGAL}
        error={{
          type: '',
          message: `${getProcedureRunConditionTextForProcedureType(procedureType, {
            capital: true,
          })} name already in use`,
        }}
        location={TriangleLocation.CELL}
      />
    );
  } else {
    return undefined;
  }
}

export function getDescErrorTriangle(
  validationStatePerCondition: Array<ConditionValidationState>,
  index: number,
  procedureType: ProcedureType
): Optional<JSX.Element> {
  if (validationStatePerCondition[index].isDescLengthOverLimit) {
    return (
      <ErrorTriangle
        dataTestAttrs={{
          component: 'ReplicateTable',
          element: 'descriptionValidation',
          index: index.toString(),
        }}
        state={TriangleState.ILLEGAL}
        error={{
          type: '',
          message: `${getProcedureRunConditionTextForProcedureType(procedureType, {
            capital: true,
          })} description may not exceed ${
            EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_DESCRIPTION_MAX_LENGTH
          } characters`,
        }}
        location={TriangleLocation.CELL}
      />
    );
  } else {
    return undefined;
  }
}

/**
 * Asserts the list is homogeneous (all `Condition` or all `ConditionGroup`) and dispatches to
 * the matching builder. Centralizes the runtime shape check so callers stay generic at the
 * type level and mixed-shape lists fail loudly instead of silently picking a branch.
 *
 * Accepts the wider `BaseCondition` element type so that generic wrappers
 * (`<TCondition extends BaseCondition>`) can pass through without re-narrowing; the runtime
 * assertion is the real safety net regardless of the static type.
 *
 * Empty lists are treated as `Condition`-shaped by convention; both builders produce `[]` from
 * an empty input, so the choice is observationally identical.
 */
export function dispatchByConditionShape<TOut>(
  conditions: ReadonlyArray<BaseCondition>,
  onConditions: (conditions: Array<Condition>) => TOut,
  onGroups: (conditions: Array<ConditionGroup>) => TOut
): TOut {
  if (conditions.length === 0) {
    return onConditions([]);
  }
  const isGroups = isConditionGroup(conditions[0]);
  assert(
    conditions.every((c) => isConditionGroup(c) === isGroups),
    'ConditionGroup and Condition shapes must not be mixed in a single conditions list.'
  );
  return isGroups
    ? onGroups(conditions as unknown as Array<ConditionGroup>)
    : onConditions(conditions as unknown as Array<Condition>);
}

/**
 * Builds {@link Condition}-shaped rows from a `ReplicateTable` edit. Used by the new-run-
 * creation flow, whose conditions are unpersisted-by-construction (no ids); for each new row
 * mapped to an existing row via `originalValueIndexes`, we carry the existing row's optional
 * `id` through so persisted ids survive renames and reorders.
 */
export function buildConditionsFromTableUpdate({
  newConditions,
  originalValueIndexes,
  conditions,
}: {
  newConditions: Array<ConditionDraft>;
  originalValueIndexes: Array<number | null>;
  conditions: Array<Condition>;
}): Array<Condition> {
  const validationStates = buildValidationStatesForUpdate(newConditions);
  return newConditions.map((condition, i) => {
    const originalIndex = originalValueIndexes[i];
    const originalCondition = originalIndex != null ? conditions[originalIndex] : undefined;
    return {
      ...condition,
      id: originalCondition?.id,
      validationState: validationStates[i],
    };
  });
}

/**
 * Builds {@link ConditionGroup}-shaped rows from a `ReplicateTable` edit. Used by the
 * plan and existing-run design tabs; for each new row mapped to an existing row via
 * `originalValueIndexes`, the existing row's `conditionIdsByPmdvId` is carried through so
 * the persisted ↔ display-row linkage survives renames and reorders. Brand-new rows get an
 * empty map or a map with null condition IDs, which downstream save serializers consume as
 * "create new backend rows".
 *
 * The read-only per-cell archive data (`replicatesByConditionId`) is keyed by the same stable
 * persisted condition ids, so it is carried through unchanged alongside `conditionIdsByPmdvId`.
 * This keeps archived cells visible in the flowchart across renames/replicate-count edits without
 * ever entering the serialization path.
 */
export function buildConditionGroupsFromTableUpdate({
  newConditions,
  originalValueIndexes,
  conditions,
  newConditionCoveredMethodIds,
}: {
  newConditions: Array<ConditionDraft>;
  originalValueIndexes: Array<number | null>;
  conditions: Array<ConditionGroup>;
  newConditionCoveredMethodIds?: ReadonlyArray<string>;
}): Array<ConditionGroup> {
  const validationStates = buildValidationStatesForUpdate(newConditions);
  return newConditions.map((condition, i) => {
    const originalIndex = originalValueIndexes[i];
    const originalCondition = originalIndex != null ? conditions[originalIndex] : undefined;
    return {
      ...condition,
      conditionIdsByPmdvId:
        originalCondition?.conditionIdsByPmdvId ??
        buildDraftConditionGroupPmdvMap(newConditionCoveredMethodIds),
      ...(originalCondition?.replicatesByConditionId != null
        ? {replicatesByConditionId: originalCondition.replicatesByConditionId}
        : {}),
      validationState: validationStates[i],
    };
  });
}

export function confirmDeleteConditions({
  unitWord,
  conditionsToRemove,
  experimentalInputs,
  procedureType,
  isProcedureRunPlan,
  doDelete,
}: {
  unitWord: 'column' | 'row';
  conditionsToRemove: Array<BaseCondition>;
  experimentalInputs: ExperimentalInputs | null;
  procedureType: ProcedureType;
  isProcedureRunPlan: boolean;
  doDelete: () => void;
}): void {
  if (!isProcedureRunPlan) {
    // Only newly added draft condition lanes can be removed. Persisted conditions cannot be deleted.
    const persistedConditionsToRemove = conditionsToRemove.filter((condition) =>
      isPersistedCondition(condition)
    );
    if (persistedConditionsToRemove.length > 0) {
      notifications.error(
        `Cannot remove saved ${getProcedureRunConditionTextForProcedureType(procedureType, {
          plural: persistedConditionsToRemove.length > 1,
        })}.`
      );
      return;
    }
  }

  const numConditionsToRemove = conditionsToRemove.length;
  const singleConditionText = getProcedureRunConditionTextForProcedureType(procedureType);
  const conditionsText = pluralize(numConditionsToRemove, singleConditionText);
  const unitsText = pluralize(numConditionsToRemove, unitWord);
  const totalUnitOps = Object.keys(experimentalInputs ?? {}).length;
  const coveredMethodIdsForRemoval = dispatchByConditionShape(
    conditionsToRemove,
    // Non-ConditionGroup flows don't track method coverage, so default to all unit ops.
    () => null,
    (conditionGroups) => {
      const removedConditionCoveredMethodIds = conditionGroups.map((conditionGroup) =>
        getMethodIdsImplementingConditionGroup(conditionGroup)
      );
      const firstCoveredMethodIds = removedConditionCoveredMethodIds[0] ?? null;
      removedConditionCoveredMethodIds.slice(1).forEach((coveredMethodIds) => {
        // Assert all removed conditions cover the same method IDs. This should hold because new
        // conditions are added either to all unit ops (split pool disabled) or to active +
        // downstream unit ops (split pool enabled), and only unpersisted conditions can be removed.
        const isMatchingCoverage =
          (firstCoveredMethodIds == null && coveredMethodIds == null) ||
          (firstCoveredMethodIds != null &&
            coveredMethodIds != null &&
            coveredMethodIds.size === firstCoveredMethodIds.size &&
            [...coveredMethodIds].every((methodId) => firstCoveredMethodIds.has(methodId)));
        assert(isMatchingCoverage, 'Expected removed conditions to cover identical method ids', {
          removedConditionCoveredMethodIds,
        });
      });
      return firstCoveredMethodIds;
    }
  );
  // If coverage is unspecified (null), treat the removal as applying across all unit ops.
  const numUnitOps = coveredMethodIdsForRemoval?.size ?? totalUnitOps;

  modals.confirm(
    `Remove ${conditionsText}?`,
    <InlineBanner
      bannerType={InlineBannerType.ERROR}
      text={`${numConditionsToRemove} ${singleConditionText} ${unitsText} across ${numUnitOps} unit ${pluralize(
        numUnitOps,
        'operation'
      )} will be lost`}
    />,
    ButtonStyle.NEGATIVE,
    {
      acceptText: `Remove ${conditionsText}`,
      rejectText: 'Back to editing',
      enableRedesign: true,
      onSubmit: () => {
        doDelete();
      },
    }
  );
}
